import React from 'react'
import { Anomaly } from '../../types/anomaly'

interface AnomalyChartProps {
  anomalies: Anomaly[]
}

const AnomalyChart: React.FC<AnomalyChartProps> = ({ anomalies }) => {
  // Group anomalies by hour for visualization
  const hourlyData = anomalies.reduce((acc, anomaly) => {
    const hour = new Date(anomaly.timestamp).getHours()
    if (!acc[hour]) {
      acc[hour] = { critical: 0, warning: 0, info: 0 }
    }
    acc[hour][anomaly.severity]++
    return acc
  }, {} as Record<number, Record<string, number>>)

  const hours = Array.from({ length: 24 }, (_, i) => i)
  const maxCount = Math.max(...Object.values(hourlyData).map(h => 
    Object.values(h).reduce((sum, count) => sum + count, 0)
  ), 1)

  return (
    <div className="h-64 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-end justify-between h-48 space-x-1">
        {hours.map(hour => {
          const data = hourlyData[hour] || { critical: 0, warning: 0, info: 0 }
          const total = data.critical + data.warning + data.info
          const height = (total / maxCount) * 100

          return (
            <div key={hour} className="flex-1 flex flex-col items-center">
              <div 
                className="w-full bg-gray-100 dark:bg-gray-700 rounded-t relative min-h-[4px]"
                style={{ height: `${Math.max(height, 4)}%` }}
              >
                {total > 0 && (
                  <div className="w-full h-full flex flex-col">
                    {/* Critical anomalies */}
                    {data.critical > 0 && (
                      <div 
                        className="bg-red-500 w-full"
                        style={{ height: `${(data.critical / total) * 100}%` }}
                        title={`Critical: ${data.critical}`}
                      />
                    )}
                    {/* Warning anomalies */}
                    {data.warning > 0 && (
                      <div 
                        className="bg-yellow-500 w-full"
                        style={{ height: `${(data.warning / total) * 100}%` }}
                        title={`Warning: ${data.warning}`}
                      />
                    )}
                    {/* Info anomalies */}
                    {data.info > 0 && (
                      <div 
                        className="bg-blue-500 w-full"
                        style={{ height: `${(data.info / total) * 100}%` }}
                        title={`Info: ${data.info}`}
                      />
                    )}
                  </div>
                )}
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {hour.toString().padStart(2, '0')}:00
              </span>
            </div>
          )
        })}
      </div>
      
      {/* Legend */}
      <div className="flex justify-center mt-3 space-x-4">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-red-500 rounded mr-2"></div>
          <span className="text-xs text-gray-600 dark:text-gray-400">Critical</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-yellow-500 rounded mr-2"></div>
          <span className="text-xs text-gray-600 dark:text-gray-400">Warning</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-blue-500 rounded mr-2"></div>
          <span className="text-xs text-gray-600 dark:text-gray-400">Info</span>
        </div>
      </div>
    </div>
  )
}

export default AnomalyChart